import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSourceHeadcount } from "@/lib/audience-sources";
import { SourcePicker } from "@/components/audience/SourcePicker";

/**
 * Where this campaign draws its audience from — the shared source list, plus
 * the number of people the selection actually adds up to.
 *
 * Selecting several sources unions them ("the four A channels plus the C one"),
 * and selecting none means the entire audience. The head-count comes from the
 * campaign preview rather than summing the per-source numbers, because a person
 * present in two channels must not be counted twice.
 */
export function SourceChannelPicker({
  channelIds,
  importSources,
  onChange,
}: {
  channelIds: string[];
  importSources: string[];
  onChange: (next: { channelIds: string[]; importSources: string[] }) => void;
}) {
  const { t } = useTranslation();
  const headcount = useSourceHeadcount();
  const selectedCount = channelIds.length + importSources.length;

  const { mutate: recount } = headcount;
  useEffect(() => {
    recount({ channel_ids: channelIds, import_sources: importSources });
  }, [recount, channelIds, importSources]);

  const people = headcount.data?.matched_users;

  return (
    <SourcePicker
      selection={{ channelIds, importSources }}
      onChange={onChange}
      footer={
        <span className="text-xs text-fg-muted">
          {headcount.isPending || people === undefined
            ? t("inviter.source.counting")
            : t(
                selectedCount > 0
                  ? "inviter.source.people_in_sources"
                  : "inviter.source.people_in_pool",
                { count: people },
              )}
        </span>
      }
    />
  );
}
